import sum from 'lodash/sum';

import type { EstimationItemResponse, UserResponse } from '@/api/schema/data-contracts';
import { ApiEstimationItemPurpose } from '@/api/schema/data-contracts';
import type { ExportRow } from '@/components/organism/DataTable/hooks/exports/model';
import { stripHtml } from '@/helpers/estimation';
import * as estimationItemHelpers from '@/helpers/estimationItemHelpers';
import type { CostModel } from '@/pages/estimation/detail/model';
import { sortCostTypes, sortResourceTypes } from '@/utils/sorting';

import type { EstimationWithDependencies } from '../useGetEstimationWithDependencies';
import type { EstimationExportOutputOptions } from './model';

const prepareExportData = (
  relevantItemsSorted: EstimationItemResponse[],
  data: EstimationWithDependencies,
  costModel: CostModel | undefined,
  outputOptions: EstimationExportOutputOptions,
  hiddenCostTypeColumns: string[],
  users: UserResponse[],
  relevantParameterNames: string[],
  onlyTotals: boolean,
  totalSumLabel: string,
) => {
  const userById = new Map(users.map((user) => [user.id, user]));

  const estimationModelItemParameterById = new Map(
    data.estimationModel.items.flatMap((x) => x.parameters).map((x) => [x.id, x]),
  );

  const methodologyParameterById = new Map(
    data.methodologies.flatMap((x) => x.defaultParameters).map((x) => [x.id, x]),
  );

  const costTypes = sortCostTypes(
    data.estimationModel.costTypes,
    data.estimationModel.costTypeGroups,
  );

  const resourceTypes = sortResourceTypes(
    data.estimationModel.resourceTypes,
    data.estimationModel.resourceTypeGroups,
  );

  const tempRelevantItemsSorted = onlyTotals
    ? // only items that don't have parent item here, i.e. top level items in context ot relevantItemsSorted (this is because we don't want to sum top level item with its child items, as the top level item's values are already sum of its children values)
      relevantItemsSorted.filter((item) => relevantItemsSorted.every((a) => a.id !== item.parentId))
    : relevantItemsSorted;

  const filteredItemsMap = new Map(tempRelevantItemsSorted.map((item) => [item.id, item]));

  // Use original order from items (same as in tables) instead of sequential order
  // This ensures export matches the table display
  const buildReference = (
    currentEstimationItem: EstimationItemResponse,
    allEstimationItems: EstimationItemResponse[],
  ): string => {
    return estimationItemHelpers.buildHierarchicalOrderReferenceString(
      currentEstimationItem,
      allEstimationItems,
    );
  };

  const exportData = tempRelevantItemsSorted.map<ExportRow>((estimationItem) => {
    const group = data.estimationModel.itemGroups.find(
      (x) => x.id === estimationItem.estimationModelGroupId,
    );
    if (!group) {
      throw new Error('Estimation group not found.');
    }
    const methodology =
      estimationItem.purpose === ApiEstimationItemPurpose.RESOURCE
        ? data.methodologies.find((x) => x.id === estimationItem.methodologyId)
        : undefined;
    const hasChildren = tempRelevantItemsSorted.some((x) => x.parentId === estimationItem.id);

    const phaseLead =
      outputOptions.showPhaseLeadColumn && estimationItem.phaseLeadId
        ? userById.get(estimationItem.phaseLeadId)
        : undefined;

    const parameters =
      outputOptions.showParameterColumns && !onlyTotals
        ? Object.fromEntries(
            relevantParameterNames.map((paramColumn) => {
              const paramForColumn = estimationItem.parameters.find((itemParam) => {
                if (itemParam.estimationModelItemParameterId) {
                  return (
                    // Same as the methodology parameter below: a parameter the export payload does
                    // not carry simply does not match this column.
                    estimationModelItemParameterById.get(itemParam.estimationModelItemParameterId)
                      ?.label === paramColumn
                  );
                }
                if (!itemParam.methodologyParameterId) {
                  throw new Error(
                    'Methodology parameter id cannot be undefined when estimation model item parameter id is undefined',
                  );
                }
                return (
                  // A parameter whose methodology is not in the export payload simply does not
                  // match this column - it must not abort the whole export.
                  methodologyParameterById.get(itemParam.methodologyParameterId)?.label ===
                  paramColumn
                );
              });
              const paramValue =
                estimationItem.isParentBasedCalculation || !hasChildren
                  ? paramForColumn?.value
                  : undefined;
              return [`parameter ${paramColumn}`, paramValue];
            }),
          )
        : {};

    const resourceColumnsValues =
      outputOptions.showResourceColumns &&
      estimationItem.purpose === ApiEstimationItemPurpose.RESOURCE
        ? Object.fromEntries(
            resourceTypes.map((resourceType) => {
              const resource = data.resources?.find(
                (r) =>
                  r.estimationItemId === estimationItem.id && r.resourceTypeId === resourceType.id,
              );
              return [`resource ${resourceType.name}`, resource?.value];
            }),
          )
        : {};

    const resourceCostColumnsValues =
      outputOptions.showResourceCostColumns &&
      costModel &&
      estimationItem.purpose === ApiEstimationItemPurpose.RESOURCE
        ? Object.fromEntries(
            costTypes
              .filter((costType) => !hiddenCostTypeColumns.includes(costType.id))
              .map((costType) => {
                const resourceTypesForCostType = resourceTypes.filter(
                  (resourceType) => resourceType.costTypeId === costType.id,
                );

                const resourcesForCostType =
                  data.resources
                    ?.filter((resource) => resource.estimationItemId === estimationItem.id)
                    .filter((resource) =>
                      resourceTypesForCostType.some(
                        (resourceType) => resourceType.id === resource.resourceTypeId,
                      ),
                    ) ?? [];
                const calculatedCost = sum(
                  resourcesForCostType.map(
                    (resource) =>
                      (resource.value ?? 0) *
                      (costModel.resourceTypeCosts.find(
                        (resourceTypeCost) =>
                          resourceTypeCost.resourceTypeId === resource.resourceTypeId,
                      )?.price ?? 0),
                  ),
                );

                return [`cost ${costType.name}`, calculatedCost];
              }),
          )
        : {};

    const directCostColumnsValues =
      outputOptions.showDirectCostColumns &&
      estimationItem.purpose === ApiEstimationItemPurpose.COST
        ? Object.fromEntries(
            costTypes
              .filter((costType) => !hiddenCostTypeColumns.includes(costType.id))
              .map((costType) => {
                const price =
                  data.estimationItemCosts?.find(
                    (x) => x.estimationItemId === estimationItem.id && x.costTypeId === costType.id,
                  )?.price ?? 0;
                return [`cost ${costType.name}`, price];
              }),
          )
        : {};

    const resourceCostSum =
      outputOptions.showCostSumColumn &&
      costModel &&
      estimationItem.purpose === ApiEstimationItemPurpose.RESOURCE
        ? {
            costSum: sum(
              data.resources
                ?.filter((resource) => resource.estimationItemId === estimationItem.id)
                .map(
                  (resource) =>
                    (resource.value ?? 0) *
                    (costModel.resourceTypeCosts.find(
                      (resourceTypeCost) =>
                        resourceTypeCost.resourceTypeId === resource.resourceTypeId,
                    )?.price ?? 0),
                ) ?? [],
            ),
          }
        : {};

    const directCostSum =
      outputOptions.showCostSumColumn && estimationItem.purpose === ApiEstimationItemPurpose.COST
        ? {
            costSum: sum(
              data.estimationItemCosts
                ?.filter((itemCost) => itemCost.estimationItemId === estimationItem.id)
                .map((itemCost) => itemCost.price ?? 0) ?? [],
            ),
          }
        : {};

    const assumptionsData =
      outputOptions.showAssumptionsColumn && !onlyTotals
        ? {
            assumptions: (() => {
              const itemAssumptions =
                data.assumptions?.filter(
                  (assumption) => assumption.estimationItemId === estimationItem.id,
                ) ?? [];

              const assumptionTexts = itemAssumptions
                .map((assumption) => stripHtml(assumption.description || '').trim())
                .filter((text) => text.length > 0);

              return assumptionTexts.length > 0 ? assumptionTexts.join('\n') : undefined;
            })(),
          }
        : {};

    return {
      reference: onlyTotals
        ? totalSumLabel
        : buildReference(estimationItem, Array.from(filteredItemsMap.values())),
      group: onlyTotals ? undefined : group.name,
      name: onlyTotals ? undefined : estimationItem.name,
      ...(outputOptions.showPhaseLeadColumn && !onlyTotals
        ? { phaseLead: phaseLead ? `${phaseLead.forename} ${phaseLead.surname}` : undefined }
        : {}),
      ...(outputOptions.showMethodologyColumn && !onlyTotals
        ? { methodology: methodology?.name ?? undefined }
        : {}),
      ...parameters,
      ...(outputOptions.showEstimateColumn
        ? {
            estimation:
              estimationItem.purpose === ApiEstimationItemPurpose.RESOURCE
                ? (estimationItem.calculatedResult ?? 0)
                : undefined,
          }
        : {}),
      ...(outputOptions.showResourceColumns
        ? {
            resourcingCheck:
              estimationItem.purpose === ApiEstimationItemPurpose.RESOURCE
                ? sum(
                    data.resources
                      ?.filter((resource) => resource.estimationItemId === estimationItem.id)
                      .map((x) => x.value ?? 0) ?? [],
                  )
                : undefined,
          }
        : {}),
      ...resourceColumnsValues,
      ...(estimationItem.purpose === ApiEstimationItemPurpose.RESOURCE
        ? resourceCostColumnsValues
        : directCostColumnsValues),
      ...(estimationItem.purpose === ApiEstimationItemPurpose.RESOURCE
        ? resourceCostSum
        : directCostSum),
      ...assumptionsData,
    };
  });

  return exportData;
};

export const prepareExportDataWithTotals = (
  relevantItemsSorted: EstimationItemResponse[],
  data: EstimationWithDependencies,
  costModel: CostModel | undefined,
  outputOptions: EstimationExportOutputOptions,
  users: UserResponse[],
  relevantParameterNames: string[],
  totalSumLabel: string,
  hiddenCostTypeColumns: string[],
) => {
  const exportData = prepareExportData(
    relevantItemsSorted,
    data,
    costModel,
    outputOptions,
    hiddenCostTypeColumns,
    users,
    relevantParameterNames,
    false,
    totalSumLabel,
  );

  const totalsRowExportData = prepareExportData(
    relevantItemsSorted,
    data,
    costModel,
    outputOptions,
    hiddenCostTypeColumns,
    users,
    relevantParameterNames,
    true,
    totalSumLabel,
  );

  const sumFields = (rows: ExportRow[]): ExportRow => {
    const total = rows.reduce(
      (acc, item) => {
        Object.keys(item).forEach((key) => {
          if (key !== 'reference') {
            if (acc[key] !== undefined || item[key] !== undefined) {
              acc[key] =
                ((acc[key] as number | undefined) ?? 0) + ((item[key] as number | undefined) ?? 0);
            }
          }
        });
        return acc;
      },
      { reference: totalSumLabel } as Record<string, number | string | undefined>,
    );

    // Ensure fields with only undefined values remain undefined
    Object.keys(total).forEach((key) => {
      if (key !== 'reference' && rows.every((item) => item[key] === undefined)) {
        total[key] = undefined;
      }
    });

    return total;
  };

  return [...exportData, sumFields(totalsRowExportData)];
};
